package com.milta.core.localization

/**
 * Strings for MIL-52 "Historique des preuves de livraison" (web dashboard, DOM-06) — both screens
 * of the flow: Screen A "Historique des tournées" (paginated list + période/livreur filters) and
 * Screen B "Preuves de la tournée" (paginated 3-column proof grid). The nav menu label itself is
 * [CommonStrings.navHistoriqueDesPreuves] (already translated) — reused as-is, not duplicated here.
 * "route not found"/"forbidden" errors on Screen B also reuse [RouteStrings.errorRouteNotFound]/
 * [RouteStrings.errorForbidden] (mirrors [com.milta.web.WebDashboardContent]'s own
 * `routeDetailErrorMessage`) — not duplicated here either.
 */
data class WebHistoriqueStrings(
    // ── Screen A — "Historique des tournées" ──
    val screenATitle: String,
    val periodLabel: String,
    val periodLastEightDays: String,
    val periodSingleDate: String,
    val periodRange: String,
    val rangeFromLabel: String,
    val rangeToLabel: String,
    val driverFilterLabel: String,
    val driverFilterAllOption: String,
    /** MIL-86 — label au-dessus du champ recherche, même convention visuelle que
     * [periodLabel]/[driverFilterLabel] (FilterLabel). */
    val historySearchLabel: String,
    /** MIL-86 — élargi (retour client) : cherche le nom du chauffeur OU, pour n'importe quelle
     * livraison de la tournée, destinataire/adresse/motif ([GetRoutesHistoryHandler] fait un
     * `Deliveries.Any(...)`). Pas de "nom de colis" : seul [PackageCount] (un entier) existe dans le
     * modèle, rien à chercher dessus. */
    val historySearchPlaceholder: String,
    val columnDate: String,
    val columnDriver: String,
    val columnTotalDeliveries: String,
    val columnDelivered: String,
    val columnFailed: String,
    val viewProofsAction: String,
    val emptyHistoryMessage: String,
    /** 422 — client-side date pickers already clamp to `[today-7, today]`, so this is only
     * reachable if the server-side rejection fires anyway (e.g. clock skew). */
    val errorInvalidPeriod: String,

    // ── Screen B — "Preuves de la tournée" ──
    val screenBTitle: String,
    /** "Tournée du" — composed in code as "$routeOfLabel $date · $driverName". */
    val routeOfLabel: String,
    /** Composed in code as "$total $totalDeliveriesLabel · $delivered $deliveredLabel · $failed $failedLabel"
     * — no template placeholder/`String.format` (JS-unsafe in commonMain), plain concatenation instead. */
    val totalDeliveriesLabel: String,
    val deliveredLabel: String,
    val failedLabel: String,
    val backToHistoryAction: String,
    val statusDeliveredBadge: String,
    val statusFailedBadge: String,
    val viewReasonAction: String,
    val hideReasonAction: String,
    val photoPlaceholderAlt: String,
    val photoAlt: String,
    val emptyProofsMessage: String,
    /** MIL-86 — label au-dessus du champ recherche de cet écran (destinataire/adresse/motif). */
    val proofsSearchLabel: String,
    /** MIL-86 — recherche par destinataire/adresse/motif. */
    val proofsSearchPlaceholder: String,

    // ── Pagination (shared by both screens) ──
    val paginationPrevious: String,
    val paginationNext: String,
)
